import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { secondAxios } from "../../../../api/axios";
import {
  PROJECT_TYPES,
  projectTypeLabel,
} from "../../../../constants/projectTypes";

const emptyForm = {
  projectName: "",
  sharepointLink: "",
  selectedType: "",
};

export default function SubProjectsCrudModal({
  open,
  plant,
  onClose,
  onProjectsChange,
}) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const clientName = process.env.REACT_APP_CLIENT_NAME;
  const plantId = plant?.id;
  const plantLabel = plant?.name || "Project";

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchProjects = useCallback(async () => {
    if (!plantId) return;
    try {
      setLoading(true);
      const res = await secondAxios.get(
        `/project/plant/${plantId}/${clientName}`,
      );
      const items = Array.isArray(res.data) ? res.data : [];
      setProjects(
        items.filter((p) => p && (!p.clientName || p.clientName === clientName)),
      );
    } catch (err) {
      console.error("Error fetching sub projects:", err);
      showSnackbar("Failed to load WBS", "error");
    } finally {
      setLoading(false);
    }
  }, [plantId, clientName]);

  useEffect(() => {
    if (open && plantId) {
      fetchProjects();
    }
    if (!open) {
      setFormOpen(false);
      setDeleteTarget(null);
      setForm(emptyForm);
      setEditingId(null);
    }
  }, [open, plantId, fetchProjects]);

  const notifyParent = async () => {
    await fetchProjects();
    if (onProjectsChange) {
      onProjectsChange();
    }
  };

  const handleOpenCreate = () => {
    setFormMode("create");
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const handleOpenEdit = (project) => {
    setFormMode("edit");
    setEditingId(project.id);
    setForm({
      projectName: project.name || project.projectName || "",
      sharepointLink: project.link || project.sharepointLink || "",
      selectedType: project.type || "",
    });
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const projectName = form.projectName.trim();
    if (!projectName) {
      showSnackbar("WBS name is required", "error");
      return;
    }
    if (formMode === "create" && !form.selectedType) {
      showSnackbar("Type is required", "error");
      return;
    }

    try {
      setSaving(true);
      if (formMode === "create") {
        await secondAxios.post(`/project/create/${clientName}`, {
          projectName,
          clientName,
          link: form.sharepointLink.trim() || "",
          type: form.selectedType,
          plantId,
        });
        showSnackbar("WBS created");
      } else {
        await secondAxios.patch(`/project/${editingId}`, {
          name: projectName,
          link: form.sharepointLink.trim() || "",
          ...(form.selectedType ? { type: form.selectedType } : {}),
        });
        showSnackbar("WBS updated");
      }
      handleCloseForm();
      await notifyParent();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        `Failed to ${formMode === "create" ? "create" : "update"} WBS`;
      showSnackbar(
        Array.isArray(message) ? message.join(", ") : message,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await secondAxios.delete(`/project/${deleteTarget.id}`);
      showSnackbar("WBS deleted");
      setDeleteTarget(null);
      await notifyParent();
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to delete WBS";
      showSnackbar(
        Array.isArray(message) ? message.join(", ") : message,
        "error",
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "WBS Code",
        accessorFn: (row) => row.name || row.projectName || "",
      },
      {
        accessorKey: "Description",
        header: "Description",
        accessorFn: (row) => row.description || "",
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 120,
        Cell: ({ cell }) => <span>{projectTypeLabel(cell.getValue())}</span>,
      },
      {
        accessorKey: "link",
        header: "SharePoint",
        Cell: ({ row }) => {
          const link = row.original.link || row.original.sharepointLink;
          if (!link) return <span>-</span>;
          return (
            <Box
              component="a"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ wordBreak: "break-all" }}
            >
              {link}
            </Box>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>WBS — {plantLabel}</DialogTitle>
        <DialogContent dividers>
          <MaterialReactTable
            data={projects}
            columns={columns}
            state={{ isLoading: loading }}
            enableRowActions
            positionActionsColumn="last"
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            initialState={{ density: "compact" }}
            renderTopToolbarCustomActions={() => (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
              >
                Add WBS
              </Button>
            )}
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenEdit(row.original)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteTarget(row.original)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="xs" fullWidth>
        <DialogTitle>
          {formMode === "create" ? "Create WBS" : "Edit WBS"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="WBS Name"
              value={form.projectName}
              onChange={(e) => handleFormChange("projectName", e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Type"
              select
              value={form.selectedType}
              onChange={(e) => handleFormChange("selectedType", e.target.value)}
              fullWidth
              required={formMode === "create"}
              disabled={formMode === "edit" && !form.selectedType}
            >
              {PROJECT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="SharePoint Link (optional)"
              value={form.sharepointLink}
              onChange={(e) =>
                handleFormChange("sharepointLink", e.target.value)
              }
              fullWidth
              type="url"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              saving ||
              !form.projectName.trim() ||
              (formMode === "create" && !form.selectedType)
            }
          >
            {formMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete WBS</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete WBS{" "}
            <strong>
              {deleteTarget?.name || deleteTarget?.projectName || ""}
            </strong>
            ? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
